/**
 * PatientTableSection Component
 * Handles patient list display with pagination and selection
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface Patient {
  patient_clinic_data_id: number;
  full_name: string;
  dob: string;
  emr_id: string;
  provider_name: string;
  primary_dx: string;
  secondary_dx: string;
}

interface PatientTableSectionProps {
  // State
  patients: Patient[];
  selectedPatientsForGroup: number[];
  selectAll: boolean;
  currentPage: number;
  totalPages: number;
  patientsPerPage: number;
  showPatientList: boolean;
  isEditMode?: boolean;
  
  // Actions
  onPatientSelection: (patientId: number, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onCurrentPageChange: (page: number) => void;
  onPatientsPerPageChange: (perPage: number) => void;
}

export const PatientTableSection: React.FC<PatientTableSectionProps> = ({
  patients,
  selectedPatientsForGroup,
  selectAll,
  currentPage,
  totalPages,
  patientsPerPage,
  showPatientList,
  isEditMode = false,
  onPatientSelection,
  onSelectAll,
  onCurrentPageChange,
  onPatientsPerPageChange
}) => {
  if (!showPatientList && !isEditMode) {
    return null;
  }

  if (patients.length === 0) {
    return null;
  }

  const startIndex = (currentPage - 1) * patientsPerPage;
  const endIndex = startIndex + patientsPerPage;
  const paginatedPatients = patients.slice(startIndex, endIndex);

  return (
    <div className={isEditMode ? "bg-white rounded-lg shadow-sm overflow-hidden" : "mt-6"}>
      <div className={isEditMode ? "overflow-x-auto" : "overflow-x-auto border border-gray-200 rounded-lg max-h-96"}>
        <table className="w-full">
          <thead className={`bg-purple-100 ${isEditMode ? "" : "sticky top-0"}`}>
            <tr>
              {!isEditMode && (
                <th className="text-left py-3 px-4 font-medium text-gray-700">
                  <input
                    type="checkbox"
                    id="select-all-patients"
                    name="selectAllPatients"
                    data-testid="select-all-patients-checkbox"
                    className="mr-2 h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 accent-blue-600"
                    checked={selectAll}
                    onChange={(e) => onSelectAll(e.target.checked)}
                  />
                  Select All
                </th>
              )}
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                Full Name
              </th>
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                DOB
              </th>
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                EMR ID
              </th>
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                Provider Name
              </th>
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                Primary Dx
              </th>
              <th className={`text-left ${isEditMode ? "py-4 px-6" : "py-3 px-4"} font-medium text-gray-700`}>
                Secondary Dx
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedPatients.map((patient) => (
              <tr key={patient.patient_clinic_data_id} className={`border-t border-gray-200 hover:bg-gray-50`}>
                {!isEditMode && (
                  <td className="py-3 px-4">
                    <input
                      type="checkbox"
                      id={`patient-checkbox-${patient.patient_clinic_data_id}`}
                      name={`patientSelection-${patient.patient_clinic_data_id}`}
                      data-testid={`patient-checkbox-${patient.patient_clinic_data_id}`}
                      className="h-4 w-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 accent-blue-600"
                      checked={selectedPatientsForGroup.includes(patient.patient_clinic_data_id)}
                      onChange={(e) => onPatientSelection(patient.patient_clinic_data_id, e.target.checked)}
                    />
                  </td>
                )}
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.full_name}
                </td>
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.dob}
                </td>
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.emr_id}
                </td>
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.provider_name}
                </td>
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.primary_dx}
                </td>
                <td className={isEditMode ? "py-4 px-6" : "py-3 px-4"}>
                  {patient.secondary_dx || 'null'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Pagination Controls */}
      <div className={`flex items-center justify-between border-t border-gray-200 bg-white px-6 py-4`}>
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-700">Rows per page:</span>
          <Select 
            value={patientsPerPage.toString()} 
            onValueChange={(value) => onPatientsPerPageChange(parseInt(value))}
          >
            <SelectTrigger className={`${isEditMode ? "w-16 h-8 text-sm" : "w-20 h-8"}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={isEditMode ? "bg-white border border-gray-200 shadow-lg z-50" : ""}>
              <SelectItem value="10" className={isEditMode ? "hover:bg-gray-100" : ""}>10</SelectItem>
              <SelectItem value="25" className={isEditMode ? "hover:bg-gray-100" : ""}>25</SelectItem>
              <SelectItem value="50" className={isEditMode ? "hover:bg-gray-100" : ""}>50</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="flex items-center space-x-4">
          {/* Page Count and Records Info */}
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <span>
              {(() => {
                const displayStartIndex = startIndex + 1;
                const displayEndIndex = Math.min(endIndex, patients.length);
                const calculatedTotalPages = Math.ceil(patients.length / patientsPerPage);
                
                if (isEditMode) {
                  return `${displayStartIndex}-${displayEndIndex} of ${patients.length} | Page ${currentPage} of ${calculatedTotalPages}`;
                } else {
                  return `${displayStartIndex}-${displayEndIndex} of ${patients.length}`;
                }
              })()}
            </span>
          </div>
          
          {/* Navigation Buttons */}
          <div className="flex items-center space-x-1">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onCurrentPageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`${isEditMode ? "h-8 px-3 text-sm" : "h-8 px-3"}`}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const calculatedTotalPages = Math.ceil(patients.length / patientsPerPage);
                onCurrentPageChange(Math.min(currentPage + 1, calculatedTotalPages));
              }}
              disabled={(() => {
                const calculatedTotalPages = Math.ceil(patients.length / patientsPerPage);
                return currentPage === calculatedTotalPages;
              })()}
              className={`${isEditMode ? "h-8 px-3 text-sm" : "h-8 px-3"}`}
            >
              Next
            </Button>
          </div>
        </div>
      </div>

      {/* Selected Patients Count - Only show in non-edit mode */}
      {!isEditMode && selectedPatientsForGroup.length > 0 && (
        <div className="text-center py-4">
          <span className="text-lg font-medium text-purple-600">
            Selected Patients: {selectedPatientsForGroup.length} (These will be added to the group)
          </span>
        </div>
      )}
    </div>
  );
};